/**
 * BotListItem — one row of the Agents page.
 *
 * Lifted out of `BotsPage`'s local `renderBotItem` closure so the White-Label
 * preview can render the REAL agent row rather than a lookalike. The preview
 * that came before this imitated `.bot-item` with invented class names and
 * inline styles, which meant a brand change could look right in the editor and
 * wrong on the page — the exact drift `LoginBrandingPanel` was extracted to
 * stop.
 *
 * Purely presentational: everything it does is a prop. It calls no service and
 * owns no state, so mounting it costs a render and nothing else.
 */

import { BotAvatar } from '@/components/common/BotAvatar';
import { useLanguage } from '@/hooks/useLanguage';
import { activeLocale } from '@/utils/locale';
import type { Bot } from '@/types/models';

/** "Today" / "3 days ago" / a date. Takes `t` rather than calling the hook so
 *  it stays a plain function, and so the wording follows the reader's
 *  language — this row is one of the most-read strings in the product. */
export function formatBotDate(dateString: string, t: (k: string, o?: unknown) => string): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffDays = Math.ceil((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays <= 1) return t('agents.today');
  if (diffDays <= 7) return t('agents.daysAgo', { count: diffDays });
  return date.toLocaleDateString(activeLocale());
}

export interface BotListItemProps {
  bot: Bot;
  onOpen?: (chatbotId: string) => void;
  onToggleFavorite?: (chatbotId: string, e: React.MouseEvent) => void;
  onEdit?: (chatbotId: string, e: React.MouseEvent) => void;
  onDuplicate?: (chatbotId: string, name: string, e: React.MouseEvent) => void;
  onShare?: (chatbotId: string, e: React.MouseEvent) => void;
  onDelete?: (chatbotId: string, name: string, e: React.MouseEvent) => void;
}

export default function BotListItem({
  bot,
  onOpen,
  onToggleFavorite,
  onEdit,
  onDuplicate,
  onShare,
  onDelete,
}: BotListItemProps) {
  const { t } = useLanguage();
  const canEdit = bot.user_permission?.can_edit ?? bot.is_owner ?? false;
  const canShare = bot.user_permission?.can_share ?? bot.is_owner ?? false;
  const canDelete = bot.user_permission?.can_delete ?? bot.is_owner ?? false;

  const avatar = bot as unknown as { avatar_color?: string; avatar_variant?: number };

  return (
    <div className="bot-item" onClick={() => onOpen?.(bot.chatbot_id)}>
      {/* Favorite Star - top right corner */}
      <button
        className={`bot-item-favorite ${bot.is_favorited ? 'favorited' : ''}`}
        onClick={e => onToggleFavorite?.(bot.chatbot_id, e)}
        title={bot.is_favorited ? t('agents.removeFromFavorites') : t('agents.addToFavorites')}
        data-testid="bot-action-favorite"
      >
        {bot.is_favorited ? '★' : '☆'}
      </button>

      {/* Card Header: Avatar + Name */}
      <div className="bot-card-header">
        <BotAvatar
          name={bot.chatbot_name || 'AI'}
          size={32}
          colorOverride={avatar.avatar_color}
          variantOverride={avatar.avatar_variant ?? undefined}
        />
        <div className="bot-item-content">
          <div className="bot-item-name" dir="auto">{bot.chatbot_name}</div>
        </div>
      </div>

      {/* Mobile Name - visible only on mobile */}
      <div className="bot-item-name-mobile" dir="auto">{bot.chatbot_name}</div>

      {/* Description. dir="auto" on the CLAMPED block itself — a line clamp
          cuts by its block's direction — and .bidi-clamp so the ellipsis
          survives in the other language's page (rtl-utils.css). */}
      <div className="bot-item-desc bidi-clamp" dir="auto">
        {bot.chatbot_description || t('agents.noDescription')}
      </div>

      {/* Meta: Date */}
      <div className="bot-item-meta">{formatBotDate(bot.created_at, t)}</div>

      {/* Action Icons - only show if user has permissions */}
      {(canEdit || canShare || canDelete) && (
        <div className="bot-item-actions">
          {canEdit && (
            <button
              className="bot-action-icon"
              onClick={e => onEdit?.(bot.chatbot_id, e)}
              title={t('agents.editAgent')}
              data-testid="bot-action-edit"
            >
              <svg viewBox="0 0 24 24">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
          )}
          {canEdit && (
            <button
              className="bot-action-icon"
              onClick={e => onDuplicate?.(bot.chatbot_id, bot.chatbot_name, e)}
              title={t('agents.duplicateAgent')}
              data-testid="bot-action-duplicate"
            >
              <svg viewBox="0 0 24 24">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
            </button>
          )}
          {canShare && (
            <button
              className="bot-action-icon"
              onClick={e => onShare?.(bot.chatbot_id, e)}
              title={t('agents.shareAgent')}
              data-testid="bot-action-share"
            >
              <svg viewBox="0 0 24 24">
                <circle cx="18" cy="5" r="3" />
                <circle cx="6" cy="12" r="3" />
                <circle cx="18" cy="19" r="3" />
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
              </svg>
            </button>
          )}
          {canDelete && (
            <button
              className="bot-action-icon delete"
              onClick={e => onDelete?.(bot.chatbot_id, bot.chatbot_name, e)}
              title={t('agents.deleteAgent')}
              data-testid="bot-action-delete"
            >
              <svg viewBox="0 0 24 24">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
